/* ==========================================================================
   Dashboard chart adapter — merges the frontend metric metadata
   (Utils/metricsConfig.js) with the backend's dynamic payload and produces the
   ECharts options + info popovers the dashboard renders.

   Backend payload (services/dashboard_analytics_service.py):
     { "kpis":   { id: value },
       "charts": { id: { labels:[], datasets:[...] } } }

   Static metadata (metricsConfig.js) per tab:
     kpis:   [{ key, title, description, formula, unit?, tone?, secondary? }]
     charts: [{ id, type, title, description, formula, group, span, height }]

   Merged chart spec handed to specToOption():
     line | area    { type, title, labels:[], datasets:[{ label, data:[] }] }
     bar            { ...same }
     bar_horizontal { ...same }
     doughnut       { ...same }
     combo          { labels:[], datasets:[{ label, data, kind:"bar"|"line", axis:"left"|"right" }] }
     bar_grouped    { labels:[], datasets:[{ label, data:[] }, ...] }
   ========================================================================== */

import * as echarts from "echarts";
import {
  buildAxisStyle,
  buildTitle,
  getAnalyticsColors,
} from "./AnalyticsConfig";
import { theme } from "../Utils/theme";
import { tabMetrics } from "./metricsConfig";

/* ------------------------------------------------------------ colour kit -- */

const palette = () => {
  const base = getAnalyticsColors();
  return base.length ? base : ["#0e7a80", "#1cbdaa", "#3b82f6", "#8b5cf6", "#f59e0b"];
};

const lift = (c, amt) => {
  try { return echarts.color.lift(c, amt); } catch (e) { return c; }
};
const fade = (c, alpha) => {
  try { return echarts.color.modifyAlpha(c, alpha); } catch (e) { return c; }
};

const barGradient = (c) => ({
  type: "linear", x: 0, y: 0, x2: 0, y2: 1,
  colorStops: [
    { offset: 0, color: lift(c, 0.24) },
    { offset: 0.6, color: c },
    { offset: 1, color: fade(lift(c, -0.05), 0.92) },
  ],
});

const hBarGradient = (c) => ({
  type: "linear", x: 0, y: 0, x2: 1, y2: 0,
  colorStops: [
    { offset: 0, color: fade(c, 0.82) },
    { offset: 1, color: lift(c, 0.16) },
  ],
});

const areaGradient = (c) => ({
  type: "linear", x: 0, y: 0, x2: 0, y2: 1,
  colorStops: [
    { offset: 0, color: fade(c, 0.34) },
    { offset: 1, color: fade(c, 0.02) },
  ],
});

const axisTooltip = (formatter) => ({
  trigger: "axis",
  axisPointer: { type: "shadow", shadowStyle: { color: `rgba(${theme.chartTooltipTextRgb()}, 0.06)` } },
  backgroundColor: theme.chartTooltipBg(),
  borderColor: theme.chartTooltipBorder(),
  borderWidth: 1,
  textStyle: { color: theme.chartTooltipText(), fontSize: 12 },
  extraCssText: `box-shadow: 0 14px 34px rgba(${theme.chartTooltipTextRgb()}, 0.18); border-radius: 12px; padding: 10px 12px;`,
  ...(formatter ? { formatter } : {}),
});

const itemTooltip = (formatter) => ({
  trigger: "item",
  backgroundColor: theme.chartTooltipBg(),
  borderColor: theme.chartTooltipBorder(),
  borderWidth: 1,
  textStyle: { color: theme.chartTooltipText(), fontSize: 12 },
  extraCssText: `box-shadow: 0 14px 34px rgba(${theme.chartTooltipTextRgb()}, 0.18); border-radius: 12px; padding: 10px 12px;`,
  ...(formatter ? { formatter } : {}),
});

const catAxis = (data, extra = {}) => ({
  type: "category",
  data,
  boundaryGap: extra.boundaryGap ?? true,
  ...buildAxisStyle(),
  axisLabel: {
    color: theme.chartAxisLabel(),
    fontSize: 12,
    hideOverlap: true,
    margin: 12,
    ...(extra.axisLabel || {}),
  },
  ...(extra.rest || {}),
});

const valAxis = (extra = {}) => ({
  type: "value",
  // dashboard values are whole counts / tokens / dollars — never show fractional
  // ticks (0.2, 0.4, …) when the whole series is small or all-zero
  minInterval: 1,
  splitNumber: 4,
  ...buildAxisStyle(),
  splitLine: { lineStyle: { color: theme.chartGridLine(), type: "dashed" } },
  axisLabel: {
    color: theme.chartAxisLabel(),
    fontSize: 12,
    margin: 14,
    formatter: (v) => (Math.abs(v) >= 10000 ? `${Math.round(v / 1000).toLocaleString("en-US")}k` : v),
    ...(extra.axisLabel || {}),
  },
  ...(extra.rest || {}),
});

// Long category labels (org / client / role names) — rotate and clamp so they
// never overlap or shrink to nothing.
const rotatedCatLabels = (labels) => {
  const longest = (labels || []).reduce((m, s) => Math.max(m, String(s).length), 0);
  const many = (labels || []).length > 6;
  if (longest <= 6 && !many) return { interval: 0 };
  return {
    interval: 0,
    rotate: longest > 10 || many ? 32 : 0,
    width: 88,
    overflow: "truncate",
    hideOverlap: true,
  };
};

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const firstDataset = (chart) => (Array.isArray(chart?.datasets) ? chart.datasets[0] : null) || { label: "Value", data: [] };

/* --------------------------------------------------------- option builders -- */

function lineOption(chart, { area }) {
  const ds = firstDataset(chart);
  const c = palette()[area ? 4 : 2];
  return {
    tooltip: axisTooltip(),
    grid: { top: 40, left: 8, right: 18, bottom: 30, containLabel: true },
    xAxis: catAxis(chart.labels || [], { boundaryGap: false }),
    yAxis: valAxis(),
    series: [
      {
        name: ds.label,
        type: "line",
        data: (ds.data || []).map(num),
        smooth: 0.38,
        symbol: "circle",
        symbolSize: 7,
        showSymbol: false,
        lineStyle: { width: 3.2, color: c, shadowBlur: 10, shadowColor: fade(c, 0.35), shadowOffsetY: 4 },
        itemStyle: { color: c, borderColor: theme.chartSurface(), borderWidth: 2 },
        areaStyle: area ? { color: areaGradient(c) } : undefined,
        emphasis: { focus: "series", scale: 1.3 },
        animationDuration: 900,
      },
    ],
  };
}

function barOption(chart) {
  const ds = firstDataset(chart);
  const cats = chart.labels || [];
  const pal = palette();
  const bars = (ds.data || []).map((v, i) => ({
    value: num(v),
    itemStyle: { color: barGradient(pal[i % pal.length]), borderRadius: [7, 7, 0, 0] },
  }));
  return {
    tooltip: axisTooltip((ps) => `${ps[0].name}<br/><b>${num(ps[0].value).toLocaleString("en-US")}</b>`),
    grid: { top: 46, left: 10, right: 16, bottom: 64, containLabel: true },
    xAxis: catAxis(cats, { axisLabel: rotatedCatLabels(cats) }),
    yAxis: valAxis(),
    series: [
      {
        name: ds.label,
        type: "bar",
        data: bars,
        barMaxWidth: 38,
        label: {
          show: cats.length <= 12,
          position: "top",
          color: theme.chartAxisLabel(),
          fontWeight: 700,
          fontSize: 11,
          formatter: ({ value }) => num(value).toLocaleString("en-US"),
        },
        emphasis: { itemStyle: { shadowBlur: 16, shadowColor: `rgba(${theme.chartTooltipTextRgb()}, 0.25)` } },
        animationDelay: (i) => i * 40,
      },
    ],
  };
}

function hBarOption(chart) {
  const ds = firstDataset(chart);
  const cats = chart.labels || [];
  const pal = palette();
  const bars = (ds.data || []).map((v, i) => ({
    value: num(v),
    itemStyle: { color: hBarGradient(pal[i % pal.length]), borderRadius: [0, 8, 8, 0] },
  }));
  return {
    tooltip: axisTooltip((ps) => `${ps[0].name}<br/><b>${num(ps[0].value).toLocaleString("en-US")}</b>`),
    grid: { top: 40, left: 8, right: 52, bottom: 18, containLabel: true },
    xAxis: valAxis(),
    yAxis: catAxis(cats, {
      rest: { inverse: true },
      axisLabel: { width: 132, overflow: "truncate", fontSize: 12 },
    }),
    series: [
      {
        name: ds.label,
        type: "bar",
        data: bars,
        barMaxWidth: 24,
        label: {
          show: true,
          position: "right",
          color: theme.chartAxisLabel(),
          fontWeight: 700,
          fontSize: 11,
          formatter: ({ value }) => num(value).toLocaleString("en-US"),
        },
        emphasis: { itemStyle: { shadowBlur: 16, shadowColor: `rgba(${theme.chartTooltipTextRgb()}, 0.25)` } },
        animationDelay: (i) => i * 40,
      },
    ],
  };
}

function doughnutOption(chart) {
  const ds = firstDataset(chart);
  const labels = chart.labels || [];
  const data = labels.map((name, i) => ({ name, value: num((ds.data || [])[i]) }));
  return {
    tooltip: itemTooltip("{b}<br/><b>{c}</b> ({d}%)"),
    legend: {
      type: "scroll",
      orient: "horizontal",
      bottom: 0,
      left: "center",
      textStyle: { color: theme.chartAxisLabel(), fontSize: 12 },
      itemWidth: 13,
      itemHeight: 13,
      itemGap: 16,
    },
    color: palette(),
    series: [
      {
        name: ds.label,
        type: "pie",
        radius: ["44%", "72%"],
        center: ["50%", "46%"],
        itemStyle: { borderColor: theme.chartSurface(), borderWidth: 2, borderRadius: 5 },
        label: { show: false },
        labelLine: { show: false },
        emphasis: {
          scale: 1.04,
          label: { show: true, fontSize: 14, fontWeight: 700, formatter: "{b}\n{d}%", color: theme.chartTitleText() },
        },
        data,
        animationType: "scale",
        animationEasing: "elasticOut",
      },
    ],
  };
}

// sits below the chart title (which specToOption pins at top:6, ~20px tall)
const topLegend = () => ({
  type: "scroll",
  top: 30,
  left: "center",
  textStyle: { color: theme.chartAxisLabel(), fontSize: 12 },
  itemWidth: 14,
  itemHeight: 10,
  itemGap: 18,
});

// combo — bars on the left axis, an overlaid line on the right axis.
function comboOption(chart) {
  const sets = Array.isArray(chart.datasets) ? chart.datasets : [];
  const cats = chart.labels || [];
  const barSet = sets.find((d) => d.kind === "bar") || sets[0] || { label: "Bars", data: [] };
  const lineSet = sets.find((d) => d.kind === "line") || sets[1] || { label: "Line", data: [] };
  const pal = palette();
  const barC = pal[2];
  const lineC = pal[4];
  const dualAxis = lineSet.axis === "right";
  return {
    tooltip: axisTooltip(),
    legend: topLegend(),
    grid: { top: 74, left: 12, right: 16, bottom: 62, containLabel: true },
    xAxis: catAxis(cats, { axisLabel: rotatedCatLabels(cats) }),
    yAxis: dualAxis
      ? [
        valAxis({
          rest: {
            name: barSet.label,
            nameGap: 14,
            nameTextStyle: { color: theme.chartAxisLabel(), fontSize: 10, align: "left" },
          },
        }),
        valAxis({
          rest: {
            name: lineSet.label,
            nameGap: 14,
            nameTextStyle: { color: theme.chartAxisLabel(), fontSize: 10, align: "right" },
            splitLine: { show: false },
          },
        }),
      ]
      : valAxis(),
    series: [
      {
        name: barSet.label,
        type: "bar",
        data: (barSet.data || []).map(num),
        barMaxWidth: 34,
        itemStyle: { color: barGradient(barC), borderRadius: [6, 6, 0, 0] },
        emphasis: { itemStyle: { shadowBlur: 16, shadowColor: fade(barC, 0.3) } },
        animationDelay: (i) => i * 40,
      },
      {
        name: lineSet.label,
        type: "line",
        yAxisIndex: dualAxis ? 1 : 0,
        data: (lineSet.data || []).map(num),
        smooth: 0.38,
        symbol: "circle",
        symbolSize: 7,
        showSymbol: false,
        lineStyle: { width: 3.2, color: lineC, shadowBlur: 10, shadowColor: fade(lineC, 0.35), shadowOffsetY: 4 },
        itemStyle: { color: lineC, borderColor: theme.chartSurface(), borderWidth: 2 },
        emphasis: { focus: "series", scale: 1.3 },
        animationDuration: 900,
      },
    ],
  };
}

// bar_grouped — 2-3 bar series side by side per category.
function groupedBarOption(chart) {
  const sets = Array.isArray(chart.datasets) ? chart.datasets : [];
  const cats = chart.labels || [];
  const pal = palette();
  return {
    tooltip: axisTooltip(),
    legend: topLegend(),
    grid: { top: 74, left: 12, right: 16, bottom: 66, containLabel: true },
    xAxis: catAxis(cats, { axisLabel: rotatedCatLabels(cats) }),
    yAxis: valAxis(),
    series: sets.map((d, si) => ({
      name: d.label,
      type: "bar",
      data: (d.data || []).map(num),
      barMaxWidth: 26,
      barGap: "18%",
      barCategoryGap: "34%",
      itemStyle: { color: barGradient(pal[si % pal.length]), borderRadius: [5, 5, 0, 0] },
      label: {
        show: cats.length <= 6 && sets.length <= 2,
        position: "top",
        color: theme.chartAxisLabel(),
        fontSize: 10,
        fontWeight: 700,
        formatter: ({ value }) => num(value).toLocaleString("en-US"),
      },
      emphasis: { itemStyle: { shadowBlur: 14, shadowColor: fade(pal[si % pal.length], 0.3) } },
      animationDelay: (i) => i * 40,
    })),
  };
}

const BUILDERS = {
  line: (c) => lineOption(c, { area: false }),
  area: (c) => lineOption(c, { area: true }),
  bar: barOption,
  bar_horizontal: hBarOption,
  doughnut: doughnutOption,
  combo: comboOption,
  bar_grouped: groupedBarOption,
};

export function specToOption(chart) {
  const builder = BUILDERS[chart?.type];
  if (!builder) return null;
  try {
    const option = builder(chart);
    // Themed title (colour-correct in both light and dark) — BaseChart renders
    // no HTML heading of its own, the title comes from the ECharts config.
    option.title = { ...buildTitle(chart.title, ""), top: 6 };
    return option;
  } catch (e) {
    console.error("Failed to build chart option for", chart?.id, e);
    return null;
  }
}

/* --------------------------------------------------------- info popovers -- */

const HOW_TO_READ = {
  line: "Each point is one month. The line rises when the value grows and falls when it drops; a steeper slope means the change is happening faster.",
  area: "Each point is one month. The shaded band under the line makes the overall direction and its size easy to follow at a glance.",
  bar: "One bar per category — the taller the bar, the larger the value. Compare the heights to rank the categories.",
  bar_horizontal: "One bar per category — the longer the bar, the larger the value. Laid out sideways so the labels stay readable.",
  doughnut: "The whole ring is everything together; each slice is one group's share of it. A bigger slice means a larger share.",
  combo: "Bars and a line share one chart. The bars use the left axis, the line uses the right axis — read each series against its own scale and watch where they move together or apart.",
  bar_grouped: "Each category has two or three bars side by side, one per series. Compare the bars within a group, then scan the same colour across groups.",
};

const HOW_CALCULATED_FALLBACK =
  "Aggregated in the database from your live records, then cached for a few minutes.";

export function describeSpec(chart) {
  return {
    title: chart.title,
    sections: [
      { h: "What this shows", b: chart.description || chart.title },
      { h: "How it's calculated", b: chart.formula || HOW_CALCULATED_FALLBACK },
      { h: "How to read it", b: HOW_TO_READ[chart.type] || "Hover any part of the chart to see the exact figures behind it." },
    ],
  };
}

export function describeKpiSpec(kpi) {
  const sections = [
    { h: "What this number means", b: kpi.description || `Where "${kpi.title}" stands right now.` },
    { h: "How it's calculated", b: kpi.formula || HOW_CALCULATED_FALLBACK },
  ];
  if (kpi.delta) {
    sections.push({ h: "The change", b: `"${kpi.delta}" is how it has moved since the previous month.` });
  }
  sections.push({ h: "The card", b: "Select the card to flip it over for more detail." });
  return { title: kpi.title, sections };
}

/* ------------------------------------------------------------ KPI mapper -- */

const TONE_CYCLE = ["teal", "blue", "violet", "green"];
const BAD_HINT = /(fail|expir|overdue|\bdue\b|at.?risk|churn|dormant|inactive|error|breach|unpaid|cancel)/i;

const formatNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value)
      ? value.toLocaleString("en-US")
      : value.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  return value == null || value === "" ? "—" : String(value);
};

// Apply a unit to a formatted number: "$" prefixes, "%" suffixes, anything else
// trails after a space. A missing value ("—") is returned untouched.
const withUnit = (value, unit) => {
  const s = formatNumber(value);
  if (s === "—") return s;
  if (unit === "$") return `$${s}`;
  if (unit === "%") return `${s}%`;
  return unit ? `${s} ${unit}` : s;
};

const deltaDirection = (delta) => {
  if (!delta) return "flat";
  const t = String(delta).trim();
  if (t.startsWith("-")) return "down";
  if (/^[+]?0(\.0+)?%?$/.test(t)) return "flat";
  return "up";
};

// `def` is a metricsConfig KPI entry already resolved with `value` and, for each
// `secondary`, its own `value`.
export function toKpiCard(def, index) {
  const title = def.title || def.key;
  const secondary = (def.secondary || []).map((s) => ({
    label: s.label,
    value: withUnit(s.value, s.unit),
  }));
  return {
    label: title,
    value: withUnit(def.value, def.unit),
    delta: def.delta || null,
    dir: deltaDirection(def.delta),
    iconKey: def.iconKey || null,
    tone: def.tone || TONE_CYCLE[index % TONE_CYCLE.length],
    bad: def.bad != null ? Boolean(def.bad) : BAD_HINT.test(title),
    spark: Array.isArray(def.trend) && def.trend.length > 1 ? def.trend.map(num) : null,
    submetrics: secondary.filter((s) => s.value !== "—"),
    back: {
      def: def.description || "",
      formula: def.formula || "",
      details: secondary,
    },
    info: describeKpiSpec(def),
  };
}

export function buildDashboard(tabKey, payload) {
  const meta = tabMetrics(tabKey);
  const kpiValues = (payload && payload.kpis) || {};
  const chartData = (payload && payload.charts) || {};
  const isOverview = tabKey === "dashboard" || tabKey === "overview";

  const kpis = meta.kpis
    .filter((def) => Object.prototype.hasOwnProperty.call(kpiValues, def.key))
    .map((def, i) =>
      toKpiCard(
        {
          ...def,
          value: kpiValues[def.key],
          secondary: (def.secondary || []).map((s) => ({ ...s, value: kpiValues[s.key] })),
        },
        i
      )
    );

  const charts = meta.charts
    .filter((def) => !isOverview || Object.prototype.hasOwnProperty.call(chartData, def.id))
    .map((def) => {
      const data = chartData[def.id];
      const spec = {
        ...def,
        labels: (data && data.labels) || [],
        datasets: (data && data.datasets) || [],
      };
      return {
        key: def.id,
        title: def.title,
        subtitle: def.description,
        type: def.type,
        group: def.group,
        span: def.span,
        height: def.height,
        option: data ? specToOption(spec) : null,
        info: describeSpec(spec),
      };
    });

  return { kpis, charts };
}

// Group built charts by their `group` label, preserving first-seen order, and
// surface the per-chart span/height the grid needs.
export function groupCharts(charts) {
  const list = Array.isArray(charts) ? charts : [];
  const order = [];
  const bucket = new Map();
  for (const c of list) {
    const g = c.group || "Analytics";
    if (!bucket.has(g)) {
      bucket.set(g, []);
      order.push(g);
    }
    bucket.get(g).push(c);
  }
  return order.map((label) => {
    const group = bucket.get(label);
    return {
      label,
      charts: group,
      spans: group.map((c) => c.span || 6),
      heights: group.map((c) => c.height || 360),
    };
  });
}
